import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/AppLayout"
import type { SelectOption } from "@/lib/options"
import { Head, useForm, usePage } from "@inertiajs/react"
import { IconArrowLeft } from "@tabler/icons-react"

interface Employee {
  id: number
  name: string
}

interface Props {
  [key: string]: unknown
  employees: Employee[]
  bill_types: SelectOption[]
  billing_frequencies: SelectOption[]
  bill: {
    bill_date: string
    due_date: string
  }
}

export default function BillNew() {
  const { employees, bill, bill_types, billing_frequencies } = usePage<Props>().props

  const { data, setData, post, processing, errors } = useForm({
    bill_type: "services",
    user_id: "",
    vendor_name: "",
    bill_date: bill.bill_date,
    due_date: bill.due_date,
    currency: "usd",
    billing_frequency: "one_time",
    subtotal: "",
    tax_amount: "",
    total_amount: "",
    description: "",
    notes: "",
    sent_to_accounting: false,
  })

  const allErrors = Object.values(errors)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post("/bills")
  }

  return (
    <>
      <Head title="New Bill - Operations Center" />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/bills">Bills</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>New Bill</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href="/bills">
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <h1 className="text-lg font-semibold tracking-tight">New Bill</h1>
          </div>
        </div>

        <div className="max-w-2xl">
          <form onSubmit={handleSubmit}>
            <Card>
              <CardHeader>
                <CardTitle>Bill Details</CardTitle>
              </CardHeader>
              <CardContent>
                {allErrors.length > 0 && (
                  <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                    <ul className="space-y-1 text-sm text-destructive">
                      {allErrors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="space-y-5">
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Bill Type</Label>
                      <Select value={data.bill_type} onValueChange={(v) => setData("bill_type", v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {bill_types.map((t) => (
                            <SelectItem key={t.value} value={t.value}>
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Billing Frequency</Label>
                      <Select value={data.billing_frequency} onValueChange={(v) => setData("billing_frequency", v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {billing_frequencies.map((f) => (
                            <SelectItem key={f.value} value={f.value}>
                              {f.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Employee</Label>
                      <Select value={data.user_id} onValueChange={(v) => setData("user_id", v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select employee (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          {employees.map((e) => (
                            <SelectItem key={e.id} value={String(e.id)}>
                              {e.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="vendor_name">Vendor Name</Label>
                      <Input
                        id="vendor_name"
                        value={data.vendor_name}
                        onChange={(e) => setData("vendor_name", e.target.value)}
                        placeholder="Vendor or supplier name"
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="bill_date">Bill Date</Label>
                      <Input
                        id="bill_date"
                        type="date"
                        value={data.bill_date}
                        onChange={(e) => setData("bill_date", e.target.value)}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="due_date">Due Date</Label>
                      <Input
                        id="due_date"
                        type="date"
                        value={data.due_date}
                        onChange={(e) => setData("due_date", e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Currency</Label>
                      <Select value={data.currency} onValueChange={(v) => setData("currency", v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="usd">USD</SelectItem>
                          <SelectItem value="uyu">UYU</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Separator />
                  <h3 className="text-sm font-semibold">Amounts</h3>

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="subtotal">Subtotal</Label>
                      <Input
                        id="subtotal"
                        type="number"
                        step="0.01"
                        min="0"
                        value={data.subtotal}
                        onChange={(e) => setData("subtotal", e.target.value)}
                        placeholder="0.00"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="tax_amount">Tax Amount</Label>
                      <Input
                        id="tax_amount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={data.tax_amount}
                        onChange={(e) => setData("tax_amount", e.target.value)}
                        placeholder="0.00"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="total_amount">Total Amount</Label>
                      <Input
                        id="total_amount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={data.total_amount}
                        onChange={(e) => setData("total_amount", e.target.value)}
                        placeholder="0.00"
                        required
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="sent_to_accounting"
                      checked={data.sent_to_accounting}
                      onCheckedChange={(checked) => setData("sent_to_accounting", checked === true)}
                    />
                    <Label htmlFor="sent_to_accounting" className="font-normal">
                      Sent to accounting
                    </Label>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      rows={2}
                      value={data.description}
                      onChange={(e) => setData("description", e.target.value)}
                      placeholder="Bill description..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      rows={2}
                      value={data.notes}
                      onChange={(e) => setData("notes", e.target.value)}
                      placeholder="Optional notes..."
                    />
                  </div>
                </div>
              </CardContent>
              <CardFooter className="justify-end gap-2 border-t">
                <Button variant="ghost" asChild>
                  <a href="/bills">Cancel</a>
                </Button>
                <Button type="submit" disabled={processing}>
                  {processing ? "Creating..." : "Create Bill"}
                </Button>
              </CardFooter>
            </Card>
          </form>
        </div>
      </div>
    </>
  )
}

BillNew.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
